/**
 * Page-level URL params Custom Dashboard supports (dates + SourceFilterModal).
 * Insight Hub drill-down params (zip, jobRun, jobCompleted, techName, …) must not
 * survive navigation back onto this route — they have no page UI and corrupt analytics.
 */
export const CUSTOM_DASHBOARD_PAGE_PARAM_KEYS = [
  /** "executive" opens the full page Executive Dashboard (see canUseExecutiveView). Absent = the widget grid. */
  "view",
  "startDate",
  "endDate",
  "dateType",
  "compareMode",
  "source",
  "sourceName",
  "serviceCategory",
  "serviceSubcategory",
  "jobCategory",
  "interactionType",
  "minRevenue",
  "maxRevenue",
  "scheduledStartDate",
  "scheduledEndDate",
] as const;

/** Value of `view` that opens the Executive Dashboard. */
export const EXECUTIVE_VIEW = "executive";

/**
 * Roles that get the Executive Dashboard: the Custom / Executive switch, the full page view, the
 * server redirect to their saved tab and the Home landing on the Custom Dashboard. One rule, read
 * by the page (browser and server) and by Home, so they can never disagree.
 */
export const EXECUTIVE_VIEW_ROLES = ["SuperAdmin", "ClientAdmin"] as const;
export const canUseExecutiveView = (role: string | null | undefined): boolean =>
  (EXECUTIVE_VIEW_ROLES as readonly string[]).includes(role ?? "");

/**
 * The widget grid tab, as saved in `DashboardConfig.dashboardView` (the URL leaves `view` out for
 * it). The page reads the saved tab on the server, so a link without `view` (Home, the sidebar, a
 * typed URL) opens the one last chosen; never chosen, they land on the Executive Dashboard.
 */
export const CUSTOM_VIEW = "custom";

const PAGE_PARAM_SET = new Set<string>(CUSTOM_DASHBOARD_PAGE_PARAM_KEYS);

/** Keep only Custom Dashboard page-level query params. */
export function pickCustomDashboardPageParams(
  input: string | URLSearchParams,
): URLSearchParams {
  const src =
    typeof input === "string" ? new URLSearchParams(input) : new URLSearchParams(input.toString());
  const out = new URLSearchParams();
  for (const key of CUSTOM_DASHBOARD_PAGE_PARAM_KEYS) {
    const value = src.get(key);
    if (value != null && value !== "") {
      out.set(key, value);
    }
  }
  return out;
}

/** True when the search string contains any key Custom Dashboard should not keep. */
export function customDashboardPageSearchNeedsSanitize(
  input: string | URLSearchParams,
): boolean {
  const src =
    typeof input === "string" ? new URLSearchParams(input) : new URLSearchParams(input.toString());
  for (const key of src.keys()) {
    if (!PAGE_PARAM_SET.has(key)) return true;
  }
  return false;
}

/**
 * Strip Insight leftover filters from a Custom Dashboard search string.
 * Returns `{ cleaned }` without a leading `?`, and `changed` when keys were removed.
 */
export function sanitizeCustomDashboardPageSearch(
  input: string | URLSearchParams,
): { cleaned: string; changed: boolean } {
  const src =
    typeof input === "string" ? new URLSearchParams(input) : new URLSearchParams(input.toString());
  const cleanedParams = pickCustomDashboardPageParams(src);
  const changed = customDashboardPageSearchNeedsSanitize(src);
  return { cleaned: cleanedParams.toString(), changed };
}
